
import React, { useState, useEffect } from 'react';
import { useBeforeAfterGallery, GalleryCase } from '@/hooks/useBeforeAfterGallery';
import { Button } from '@/components/ui/button';
import { Plus, Image } from 'lucide-react';
import DoctorHeader from '@/components/doctor/DoctorHeader';
import DoctorFooterNav from '@/components/doctor/DoctorFooterNav';
import AddCaseModal from './components/AddCaseModal';
import GalleryFilters from './components/GalleryFilters';
import GalleryGrid from './components/GalleryGrid';
import { BeforeAfterCase, GalleryFilters as GalleryFilterTypes } from '@/types/gallery';

const DoctorBeforeAfterGallery: React.FC = () => {
  const { 
    gallery, 
    cases, 
    loading, 
    filters: apiFilters, 
    setFilters: updateApiFilters, 
    addCase, 
    updateCase, 
    deleteCase, 
    toggleVisibility,
    clinics,
    treatmentTypes
  } = useBeforeAfterGallery();
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingCase, setEditingCase] = useState<BeforeAfterCase | undefined>(undefined);

  // Local UI filters, renamed to avoid conflict
  const [uiFilters, setUiFilters] = useState<GalleryFilterTypes>({
    visibility: 'all',
    sortBy: 'date',
    sortDirection: 'desc'
  });

  const handleAddOrUpdateCase = (caseData: Omit<BeforeAfterCase, 'id' | 'doctorId' | 'createdAt' | 'updatedAt'>) => {
    const galleryCase = {
      ...caseData,
      description: caseData.description || '',
    };
    
    if (editingCase && editingCase.id) {
      updateCase(editingCase.id, galleryCase);
    } else {
      addCase(galleryCase);
    }
    setEditingCase(undefined);
  };

  const handleEditCase = (caseItem: BeforeAfterCase) => {
    setEditingCase(caseItem);
    setIsAddModalOpen(true);
  };

  // Ensure all cases have required display properties
  const displayCases: BeforeAfterCase[] = cases.map(caseItem => ({
    ...caseItem,
    id: caseItem.id || `temp-${Date.now()}`,
    createdAt: caseItem.createdAt || new Date().toISOString(),
    updatedAt: caseItem.updatedAt || caseItem.createdAt?.toString() || new Date().toISOString()
  }));

  return (
    <div className="min-h-screen bg-background pb-24">
      <DoctorHeader />
      
      <div className="container mx-auto px-4 pt-20 pb-24">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Image className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">Before & After Gallery</h1>
          </div>
          
          <Button 
            onClick={() => {
              setEditingCase(undefined);
              setIsAddModalOpen(true);
            }}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add New Case
          </Button>
        </div>
        
        <p className="text-muted-foreground mb-6">
          Showcase your work by adding before and after images of your treatments.
          Public cases will appear on your doctor profile and in the global gallery.
        </p>
        
        <GalleryFilters 
          filters={uiFilters} 
          onFilterChange={(newFilters) => setUiFilters({...uiFilters, ...newFilters})}
          clinics={clinics}
          treatmentTypes={treatmentTypes}
        />
        
        <GalleryGrid 
          cases={displayCases}
          onEdit={handleEditCase}
          onDelete={deleteCase}
          onToggleVisibility={toggleVisibility}
        />
        
        <AddCaseModal 
          open={isAddModalOpen} 
          onOpenChange={setIsAddModalOpen}
          onSubmit={handleAddOrUpdateCase}
          clinics={clinics}
          editCase={editingCase}
        />
      </div>
      
      <DoctorFooterNav />
    </div>
  );
};

export default DoctorBeforeAfterGallery;
